/**
 * org-ai-cards.tsx — Các khối cấp TỔ CHỨC của trang Nguồn AI và lượt AI (khung ba tầng):
 *  - "Tạm dừng toàn bộ AI" (= autoReplyEnabled=false, nhãn đảo nghĩa) + cảnh báo AI tổ chức đang tắt / Platform khoá;
 *  - "Giới hạn" (chỉ ô của nguồn đang dùng: credit / ngày cho AI của eCDP, lượt / ngày cho Key của tôi; trần theo gói và
 *    trần Platform chỉ đọc);
 *  - "AI hỗ trợ nhân viên": mặc định theo model của tổ chức (bước 2 của source-mode-card); model riêng từng việc gập trong
 *    "Nâng cao";
 *  - dòng giải thích: cách AI trả lời khách nay cài ở từng nhân viên AI.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Bot, ChevronDown, Gauge, PauseCircle, Save, Sparkles } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AiModelPicker } from '@/components/ai/ai-model-picker'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { aiKeys, updateAiConfig, type AiConfig, type TaskOverrides } from '@/hooks/use-ai'
import { useAiCredits, useAiModels, useUpdateCreditSettings } from '@/hooks/use-ai-credits'
import { useEnableOrgAi, useSetAiPausedAll } from '@/hooks/use-ai-khung'
import { currentLocale } from '@/lib/i18n'
import { aiErrorMessage, amountText, findModel, formatTurns, tokenRateOf, unitsToTurns } from '@/lib/ai-credits'
import { AI_PATHS } from '@/lib/ai-khung'
import { isLegacyAiProvider, legacyProviderWarning } from '@/lib/ai-providers'
import { cn } from '@/lib/utils'
import { applyStaffTask, STAFF_ASSIST_TASKS, staffTaskValue } from './staff-assist-lib'
import type { SourceMode } from './source-mode-lib'

// ── Tạm dừng toàn bộ AI ──────────────────────────────────────────────

export function PauseAllCard({ config, canEdit }: { config: AiConfig; canEdit: boolean }) {
  const { t } = useTranslation('aiOps')
  const pauseAll = useSetAiPausedAll()
  const enableAi = useEnableOrgAi()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const paused = !config.autoReplyEnabled

  const apply = (next: boolean) =>
    pauseAll.mutate(next, {
      onSuccess: () => {
        setConfirmOpen(false)
        toast.success(next ? t('orgAiCards.pause.pausedToast') : t('orgAiCards.pause.resumedToast'))
      },
      onError: (err) => toast.error(aiErrorMessage(err)),
    })

  return (
    <Card className={paused ? 'border-warning/50' : undefined}>
      <CardContent className="space-y-3 py-4">
        {config.platformAiDisabled && (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <span><Trans t={t} i18nKey="orgAiCards.pause.platformLocked" components={{ b: <b /> }} /></span>
          </p>
        )}
        {!config.enabled && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
            <span className="flex-1">
              <Trans t={t} i18nKey="orgAiCards.pause.orgDisabled" components={{ b: <b /> }} />
            </span>
            {canEdit && (
              <Button
                size="sm"
                disabled={enableAi.isPending}
                onClick={() => enableAi.mutate(undefined, {
                  onSuccess: () => toast.success(t('orgAiCards.pause.reEnabledToast')),
                  onError: (err) => toast.error(aiErrorMessage(err)),
                })}
              >
                {t('orgAiCards.pause.reEnable')}
              </Button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="ai-pause-all" className="flex items-center gap-2 text-sm font-semibold">
              <PauseCircle className={paused ? 'h-4 w-4 text-warning' : 'h-4 w-4 text-muted-foreground'} /> {t('orgAiCards.pause.title')}
              {paused && <Badge variant="warning">{t('orgAiCards.pause.badge')}</Badge>}
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('orgAiCards.pause.desc')}
            </p>
          </div>
          {canEdit && (
            <Switch
              id="ai-pause-all"
              checked={paused}
              disabled={pauseAll.isPending}
              onCheckedChange={(v) => (v ? setConfirmOpen(true) : apply(false))}
            />
          )}
        </div>
      </CardContent>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('orgAiCards.pause.confirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('orgAiCards.pause.confirmDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>{t('common:actions.cancel')}</Button>
            <Button variant="destructive" disabled={pauseAll.isPending} onClick={() => apply(true)}>
              {pauseAll.isPending ? t('common:status.saving') : t('orgAiCards.pause.title')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// ── Giới hạn ─────────────────────────────────────────────────────────

/**
 * `mode` (nguồn tổ chức đang dùng): AI của eCDP → chỉ mức tiêu / ngày (nhập theo đ, giá bán ra; lưu theo đơn vị nội bộ của
 * ví); Key của tôi → chỉ lượt / ngày; kết hợp → cả hai.
 */
export function LimitsCard({ config, canEdit, mode }: { config: AiConfig; canEdit: boolean; mode?: SourceMode }) {
  const { t } = useTranslation('aiOps')
  const qc = useQueryClient()
  const { data: credits } = useAiCredits()
  const updateCreditSettings = useUpdateCreditSettings()
  const [maxDaily, setMaxDaily] = useState(String(config.maxDaily))
  const [dailyCap, setDailyCap] = useState('')
  const [saving, setSaving] = useState(false)
  const showCredit = !!credits && mode !== 'byok'
  const showTurns = mode !== 'platform'
  const rate = tokenRateOf(credits)
  const unitVnd = rate?.creditValueVnd || 0
  // Ô "AI của eCDP" nhập theo LƯỢT AI / ngày (đơn vị khách thấy); lưu về đơn vị nội bộ của ví.
  const turnVnd = rate?.turnValueVnd || 0

  useEffect(() => setMaxDaily(String(config.maxDaily)), [config.maxDaily])
  useEffect(
    () => setDailyCap(credits?.dailyCap != null && unitVnd && turnVnd ? String(Math.round((credits.dailyCap * unitVnd) / turnVnd)) : ''),
    [credits?.dailyCap, unitVnd, turnVnd],
  )

  async function save() {
    const turns = Number(maxDaily)
    if (showTurns && (!Number.isInteger(turns) || turns < 1)) return void toast.error(t('orgAiCards.limits.turnsInvalid'))
    const capText = dailyCap.trim()
    const capTurns = capText === '' ? null : Number(capText)
    if (capTurns !== null && (!Number.isFinite(capTurns) || capTurns < 0)) return void toast.error(t('orgAiCards.limits.capInvalid'))
    // lượt → đơn vị nội bộ của ví (3 số lẻ).
    const cap = capTurns === null || !unitVnd || !turnVnd ? null : Math.round(((capTurns * turnVnd) / unitVnd) * 1000) / 1000
    setSaving(true)
    try {
      if (showTurns && turns !== config.maxDaily) await updateAiConfig({ maxDaily: turns })
      if (showCredit && credits && cap !== (credits.dailyCap ?? null)) await updateCreditSettings.mutateAsync({ dailyCap: cap })
      await qc.invalidateQueries({ queryKey: aiKeys.config })
      toast.success(t('orgAiCards.limits.saved'))
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> {t('orgAiCards.limits.title')}</CardTitle>
        <CardDescription>{t('orgAiCards.limits.desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={showCredit && showTurns ? 'grid gap-4 sm:grid-cols-2' : ''}>
          {showCredit && credits && (
            <div className="space-y-1">
              <Label htmlFor="ai-daily-cap">{t('orgAiCards.limits.platformLabel')}</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="ai-daily-cap" type="number" min={0} step={10} value={dailyCap} placeholder={t('orgAiCards.limits.notSet')}
                  onChange={(e) => setDailyCap(e.target.value)} className="max-w-36" disabled={!canEdit}
                />
                <span className="text-sm text-muted-foreground">{t('orgAiCards.limits.turnsPerDayAi')}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('orgAiCards.limits.usedToday', { amount: formatTurns(unitsToTurns(credits.usedToday, rate)) })}
              </p>
              {credits.planDailyCap != null && (
                <p className="text-xs text-muted-foreground">
                  <Trans
                    t={t} i18nKey="orgAiCards.limits.planCap"
                    values={{ plan: credits.plan?.name ? ` ${credits.plan.name}` : '', amount: amountText(credits.planDailyCap, rate) }}
                    components={{ b: <b /> }}
                  />
                  {credits.effectiveDailyCap != null && credits.effectiveDailyCap !== credits.planDailyCap
                    ? t('orgAiCards.limits.planCapApplied', { amount: amountText(credits.effectiveDailyCap, rate) })
                    : ''}.
                </p>
              )}
            </div>
          )}
          {showTurns && <div className="space-y-1">
            <Label htmlFor="ai-max-daily">{t('orgAiCards.limits.byokLabel')}</Label>
            <div className="flex items-center gap-3">
              <Input
                id="ai-max-daily" type="number" min={1} max={10000} value={maxDaily}
                onChange={(e) => setMaxDaily(e.target.value)} className="max-w-36" disabled={!canEdit}
              />
              <span className="text-sm text-muted-foreground">{t('orgAiCards.limits.turnsPerDay')}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('orgAiCards.limits.byokHint')}</p>
            {config.platformMaxDaily != null && (
              <p className="text-xs text-warning">
                <Trans t={t} i18nKey="orgAiCards.limits.platformLimit" values={{ n: config.platformMaxDaily.toLocaleString(currentLocale()) }} components={{ b: <b /> }} />
              </p>
            )}
          </div>}
        </div>
        {canEdit && (
          <Button onClick={() => void save()} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? t('common:status.saving') : t('orgAiCards.limits.save')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

// ── AI hỗ trợ nhân viên ──────────────────────────────────────────────

export function StaffAssistCard({ config, canEdit }: { config: AiConfig; canEdit: boolean }) {
  const { t } = useTranslation('aiOps')
  const qc = useQueryClient()
  const { data: modelsData } = useAiModels()
  const [overrides, setOverrides] = useState<TaskOverrides>({ ...(config.taskOverrides ?? {}) })
  const [saving, setSaving] = useState(false)
  const custom = STAFF_ASSIST_TASKS.filter((task) => staffTaskValue(config.taskOverrides, task)).length
  const [advanced, setAdvanced] = useState(custom > 0)
  const orgModelName = findModel(modelsData?.models ?? [], config.provider, config.model)?.title ?? config.model

  useEffect(() => setOverrides({ ...(config.taskOverrides ?? {}) }), [config])

  async function save() {
    if (STAFF_ASSIST_TASKS.some((task) => isLegacyAiProvider(overrides[task.key]?.provider))) return void toast.error(legacyProviderWarning())
    setSaving(true)
    try {
      // Gửi đủ task_overrides: khoá của nhân viên AI (auto_reply, ai_router) giữ nguyên như đang lưu.
      await updateAiConfig({ taskOverrides: overrides })
      await qc.invalidateQueries({ queryKey: aiKeys.config })
      toast.success(t('orgAiCards.staff.saved'))
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> {t('orgAiCards.staff.title')}</CardTitle>
        <CardDescription>
          <Trans t={t} i18nKey="orgAiCards.staff.desc" values={{ model: orgModelName }} components={{ b: <b className="text-foreground" /> }} />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border">
          <button type="button" onClick={() => setAdvanced((o) => !o)} aria-expanded={advanced}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40">
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', advanced && 'rotate-180')} />
            {t('orgAiCards.staff.advanced')}{custom > 0 ? t('orgAiCards.staff.advancedCustom', { count: custom }) : ''}
          </button>
          {advanced && (
            <div className="space-y-3 border-t p-3">
              {STAFF_ASSIST_TASKS.map((task) => {
                const v = staffTaskValue(overrides, task)
                return (
                  <div key={task.key} className={`rounded-lg border p-3 ${v ? 'border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20' : ''}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <span>{task.icon}</span>
                      <span className="text-sm font-semibold">{task.name}</span>
                      {v ? (
                        canEdit && (
                          <button
                            type="button" className="ml-auto text-xs font-medium text-primary hover:underline disabled:opacity-50"
                            onClick={() => setOverrides((cur) => applyStaffTask(cur, task, null))}
                          >
                            {t('orgAiCards.staff.followOrg')}
                          </button>
                        )
                      ) : (
                        <Badge variant="outline" className="ml-auto">{t('orgAiCards.staff.followOrg')}</Badge>
                      )}
                    </div>
                    <AiModelPicker
                      value={v}
                      onChange={(nv) => setOverrides((cur) => applyStaffTask(cur, task, nv))}
                      allowInherit
                      inheritLabel={t('orgAiCards.staff.inheritLabel', { model: orgModelName })}
                      disabled={!canEdit}
                      ariaLabel={t('orgAiCards.staff.modelFor', { name: task.name })}
                      showInfo={!!v}
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">{task.desc}</p>
                  </div>
                )
              })}
              {canEdit && (
                <Button onClick={() => void save()} disabled={saving}>
                  <Save className="h-4 w-4" /> {saving ? t('common:status.saving') : t('orgAiCards.staff.save')}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ── Dòng giải thích: cài đặt đã dời về từng nhân viên AI ─────────────

export function MovedToBotsNote() {
  const { t } = useTranslation('aiOps')
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm">
      <Bot className="h-4 w-4 shrink-0 text-primary" />
      <p className="min-w-0 flex-1 text-muted-foreground">
        <Trans t={t} i18nKey="orgAiCards.movedNote" components={{ b: <b className="text-foreground" /> }} />
      </p>
      <OpenInNewTab to={AI_PATHS.bots} variant="outline" size="sm" title={t('orgAiCards.openBots.title')}>{t('orgAiCards.openBots.label')}</OpenInNewTab>
    </div>
  )
}
